import { ArrowsPointingIn, ArrowsPointingOut, Paperclip, Pen, Trash } from "@ignite-analytics/icons";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    Divider,
    FormControlLabel,
    IconButton,
    Link,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { retryServerErrors } from "../../lib/formsApi";
import type { InputProps } from "../answers";
import { type LoadedCodeOfConduct, useCodeOfConductSource } from "../codeOfConduct";
import { DocumentCard } from "../DocumentCard";
import { FilePicker } from "../FilePicker";
import { useReadOnly } from "../readOnly";

type File = NonNullable<LoadedCodeOfConduct["file"]>;

const SignDialog: React.FC<{
    file: File;
    open: boolean;
    onClose: () => void;
    supplierName: string;
    signingAs?: string;
    signerName?: string;
    viewOnly: boolean;
    onSign: (name: string) => void;
}> = ({ file, open, onClose, supplierName, signingAs, signerName, viewOnly, onSign }) => {
    const { formatMessage } = useIntl();
    const [step, setStep] = useState<"read" | "sign">("read");
    const [enlarged, setEnlarged] = useState(false);
    const [name, setName] = useState(signerName ?? "");
    const [authority, setAuthority] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const ready = !!name.trim() && authority && accepted;

    const close = () => {
        setStep("read");
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={close}
            fullScreen={enlarged}
            fullWidth
            maxWidth="md"
            slotProps={{ paper: { sx: enlarged ? undefined : { height: "85vh" } } }}
        >
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", px: 2, py: 1 }}>
                <Typography variant="textMd" noWrap sx={{ flex: 1 }}>
                    {file.name}
                </Typography>
                <Tooltip
                    title={
                        enlarged
                            ? formatMessage({ defaultMessage: "Shrink" })
                            : formatMessage({ defaultMessage: "Enlarge" })
                    }
                >
                    <IconButton size="small" onClick={() => setEnlarged(!enlarged)}>
                        {enlarged ? <ArrowsPointingIn fontSize="small" /> : <ArrowsPointingOut fontSize="small" />}
                    </IconButton>
                </Tooltip>
                <Button color="secondary" onClick={close}>
                    <FormattedMessage defaultMessage="Close" />
                </Button>
            </Stack>
            <Divider />
            {step === "read" ? (
                <Box component="iframe" src={file.blobUrl} title={file.name} sx={{ flex: 1, border: 0 }} />
            ) : (
                <Stack spacing={1.5} sx={{ flex: 1, alignItems: "flex-start", px: 3, py: 3, overflowY: "auto" }}>
                    <Typography variant="textSm">
                        <FormattedMessage
                            defaultMessage="You are signing {fileName} on behalf of {supplierName}."
                            values={{ fileName: <b>{file.name}</b>, supplierName: <b>{supplierName}</b> }}
                        />
                    </Typography>
                    <TextField
                        size="small"
                        disabled
                        label={formatMessage({ defaultMessage: "Email" })}
                        value={signingAs ?? ""}
                        helperText={
                            signingAs
                                ? undefined
                                : formatMessage({ defaultMessage: "The email the request was sent to." })
                        }
                        sx={{ width: "100%", maxWidth: 400 }}
                    />
                    <TextField
                        size="small"
                        autoFocus
                        label={formatMessage({ defaultMessage: "Full name" })}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        sx={{ width: "100%", maxWidth: 400 }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox checked={authority} onChange={(event) => setAuthority(event.target.checked)} />
                        }
                        label={
                            <FormattedMessage
                                defaultMessage="I have the authority to sign on behalf of {supplierName}"
                                values={{ supplierName }}
                            />
                        }
                    />
                    <FormControlLabel
                        control={
                            <Checkbox checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
                        }
                        label={<FormattedMessage defaultMessage="I accept the Code of Conduct" />}
                    />
                </Stack>
            )}
            {!viewOnly && (
                <>
                    <Divider />
                    <Stack
                        direction="row"
                        sx={{ alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5 }}
                    >
                        <Typography variant="textSm" color="textTextSecondary">
                            <FormattedMessage
                                defaultMessage="Step {step} of 2"
                                values={{ step: step === "read" ? 1 : 2 }}
                            />
                        </Typography>
                        {step === "read" ? (
                            <Button variant="contained" onClick={() => setStep("sign")}>
                                <FormattedMessage defaultMessage="Continue to sign" />
                            </Button>
                        ) : (
                            <Stack direction="row" spacing={1}>
                                <Button color="secondary" onClick={() => setStep("read")}>
                                    <FormattedMessage defaultMessage="Back to document" />
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={!ready}
                                    onClick={() => {
                                        onSign(name.trim());
                                        close();
                                    }}
                                >
                                    <FormattedMessage defaultMessage="Sign" />
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                </>
            )}
        </Dialog>
    );
};

const Detail: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({ label, children }) => (
    <>
        <Typography
            variant="textXs"
            color="textTextSecondary"
            sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
        >
            {label}
        </Typography>
        <Typography variant="textXs" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
            {children}
        </Typography>
    </>
);

const SignatureMark: React.FC<{
    name: string;
    email?: string;
    supplierName: string;
    file: File;
    signedAt?: Date;
    onOpen: () => void;
    onRemove?: () => void;
}> = ({ name, email, supplierName, file, signedAt, onOpen, onRemove }) => (
    <Box
        sx={{
            width: "100%",
            maxWidth: 560,
            border: "3px double",
            borderColor: "divider",
            borderRadius: 1,
            px: 2.5,
            py: 2,
        }}
    >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start", mb: 1.5 }}>
            <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="textXs"
                    color="textTextSecondary"
                    sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em" }}
                >
                    <FormattedMessage defaultMessage="Electronically signed" />
                </Typography>
                <Typography variant="textLg" sx={{ fontWeight: 600 }}>
                    {name}
                </Typography>
                <Typography variant="textSm" color="textTextSecondary">
                    <FormattedMessage defaultMessage="On behalf of {supplierName}" values={{ supplierName }} />
                </Typography>
                {onRemove && (
                    <Typography variant="textXs" color="textTextSecondary" sx={{ mt: 0.5 }}>
                        <FormattedMessage defaultMessage="Sent with your answers when you submit. Until then you can remove it." />
                    </Typography>
                )}
            </Stack>
            {onRemove && (
                <Tooltip title={<FormattedMessage defaultMessage="Remove signature" />}>
                    <IconButton size="small" onClick={onRemove}>
                        <Trash fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ display: "grid", gridTemplateColumns: "max-content minmax(0, 1fr)", columnGap: 2, rowGap: 0.75 }}>
            <Detail label={<FormattedMessage defaultMessage="Email" />}>{email ?? "-"}</Detail>
            <Detail label={<FormattedMessage defaultMessage="Signed at" />}>
                {signedAt ? signedAt.toISOString().replace(/\.\d+Z$/, "Z") : "-"}
            </Detail>
            <Detail label={<FormattedMessage defaultMessage="Document" />}>{file.name}</Detail>
            <Detail label={<FormattedMessage defaultMessage="SHA-256" />}>{file.sha256}</Detail>
            <Detail label={<FormattedMessage defaultMessage="IP address" />}>
                <FormattedMessage defaultMessage="Recorded when the form is submitted" />
            </Detail>
            <Detail label={<FormattedMessage defaultMessage="Browser" />}>{navigator.userAgent}</Detail>
        </Box>
        <Box sx={{ mt: 2 }}>
            <DocumentCard name={file.name} sizeBytes={file.sizeBytes} onOpen={onOpen} />
        </Box>
        <Typography variant="textXs" color="textTextSecondary" sx={{ display: "block", mt: 1.5 }}>
            <FormattedMessage
                defaultMessage="<link>Simple electronic signature (SES)</link> under eIDAS."
                values={{
                    link: (chunks) => (
                        <Link
                            href="https://digital-strategy.ec.europa.eu/en/policies/eidas-regulation"
                            target="_blank"
                            rel="noopener noreferrer"
                            color="inherit"
                        >
                            {chunks}
                        </Link>
                    ),
                }}
            />
        </Typography>
    </Box>
);

export const SignatureInput: React.FC<InputProps<"signature">> = ({ question, answer, onChange, error }) => {
    const { supplierName, key, digitalSigning, signingAs, signerName, load } = useCodeOfConductSource();
    const readOnly = useReadOnly();
    const { data, isPending, isError, refetch } = useQuery({
        queryKey: ["codeOfConduct", key],
        queryFn: load,
        staleTime: Infinity,
        retry: retryServerErrors,
    });
    const signedBy = typeof answer?.value === "string" ? answer.value : "";
    const files = answer?.files ?? [];
    const digitalAllowed = digitalSigning && !!data?.file;
    const [uploading, setUploading] = useState(files.length > 0);
    const [open, setOpen] = useState(false);
    const [signedAt, setSignedAt] = useState<Date>();
    const digital = digitalAllowed && !uploading;

    const setValue = (value: string) => onChange((current) => ({ ...current, value }));
    const switchTo = (upload: boolean) => {
        setUploading(upload);
        setValue("");
    };

    if (isPending) return <Skeleton variant="text" width={240} />;
    if (isError)
        return (
            <Alert
                severity="error"
                action={
                    <Button color="inherit" size="small" onClick={() => refetch()}>
                        <FormattedMessage defaultMessage="Try again" />
                    </Button>
                }
            >
                <FormattedMessage defaultMessage="Could not load the Code of Conduct." />
            </Alert>
        );

    return (
        <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
            {!data.file && (
                <Alert severity="warning">
                    <FormattedMessage defaultMessage="The Code of Conduct is not available. Please contact the buyer." />
                </Alert>
            )}
            {digital && data.file ? (
                <>
                    <SignDialog
                        file={data.file}
                        open={open}
                        onClose={() => setOpen(false)}
                        supplierName={supplierName}
                        signingAs={signingAs}
                        signerName={signerName}
                        viewOnly={!!signedBy}
                        onSign={(name) => {
                            setSignedAt(new Date());
                            setValue(name);
                        }}
                    />
                    {signedBy ? (
                        <SignatureMark
                            name={signedBy}
                            email={signingAs}
                            supplierName={supplierName}
                            file={data.file}
                            signedAt={signedAt}
                            onOpen={() => setOpen(true)}
                            onRemove={
                                readOnly
                                    ? undefined
                                    : () => {
                                          setSignedAt(undefined);
                                          setValue("");
                                      }
                            }
                        />
                    ) : (
                        <Button
                            size="small"
                            variant="outlined"
                            color={error ? "error" : "secondary"}
                            startIcon={<Pen />}
                            onClick={() => setOpen(true)}
                        >
                            <FormattedMessage defaultMessage="Read and sign" />
                        </Button>
                    )}
                    {!readOnly && (
                        <Link component="button" type="button" variant="textSm" onClick={() => switchTo(true)}>
                            <FormattedMessage defaultMessage="Upload signed copy instead" />
                        </Link>
                    )}
                </>
            ) : (
                <>
                    {data.file && (
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                            <Paperclip fontSize="small" />
                            <Link variant="textSm" href={data.file.blobUrl} download={data.file.name}>
                                {data.file.name}
                            </Link>
                        </Stack>
                    )}
                    {!readOnly && (
                        <Typography variant="textSm" color="textTextSecondary">
                            <FormattedMessage defaultMessage="Upload a signed copy of the Code of Conduct." />
                        </Typography>
                    )}
                    <FilePicker
                        questionKey={question.id}
                        answer={answer}
                        files={files}
                        error={error}
                        onChange={(update) =>
                            onChange((current) => ({ ...current, value: "", files: update(current?.files ?? []) }))
                        }
                    />
                    {!readOnly && digitalAllowed && files.length === 0 && (
                        <Link component="button" type="button" variant="textSm" onClick={() => switchTo(false)}>
                            <FormattedMessage defaultMessage="Sign digitally instead" />
                        </Link>
                    )}
                </>
            )}
        </Stack>
    );
};
